import { useState } from "react";
import { toast } from "sonner";
import { FileDown } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { EmailToClient } from "@/components/EmailToClient";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { downloadPdf, errMsg } from "@/lib/api";

const FIELDS = [
  ["prepared_for", "Prepared for", "e.g. John D. & Emily S. Rockefeller"],
  ["advisor", "Advisor name", "e.g. Jane Smith, CPA"],
  ["contact", "Advisor contact", "e.g. jane@yourfirm.com · (555) 010-2000"],
];

export const FamilyBookButton = ({ api, period, tenantId, defaultFor }) => {
  const key = `avora-book-cover-${tenantId}`;
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [v, setV] = useState({});
  const [firm, setFirm] = useState(null);
  const [saveFirm, setSaveFirm] = useState(true);
  const show = async () => {
    let saved = {};
    try { saved = JSON.parse(localStorage.getItem(key) || "{}"); } catch { saved = {}; }
    let f = { advisor: "", contact: "", can_edit: false };
    try { f = (await api.get("/firm/book-cover")).data; } catch { /* use blanks */ }
    setFirm(f);
    setSaveFirm(true);
    setV({ prepared_for: saved.prepared_for ?? defaultFor ?? "", advisor: f.advisor, contact: f.contact });
    setOpen(true);
  };
  const go = async () => {
    setBusy(true);
    localStorage.setItem(key, JSON.stringify({ prepared_for: v.prepared_for }));
    const q = new URLSearchParams({ period, ...v }).toString();
    try {
      if (firm?.can_edit && saveFirm && (v.advisor !== firm.advisor || v.contact !== firm.contact)) {
        await api.put("/firm/book-cover", { advisor: v.advisor, contact: v.contact });
        toast.success("Advisor details saved for everyone in the firm");
      }
      await downloadPdf(api, `/reports/family/pdf?${q}`, `Family_Statement_Book_${period}.pdf`);
      setOpen(false);
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setBusy(false);
    }
  };
  return (
    <>
      <Button onClick={show} className="rounded-sm" data-testid="dash-family-book"><FileDown className="h-4 w-4 mr-2" />Download family book</Button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="rounded-sm sm:max-w-md max-h-[90vh] overflow-y-auto" data-testid="family-book-dialog">
          <DialogHeader>
            <DialogTitle className="font-serif text-2xl">Family book cover</DialogTitle>
            <DialogDescription>These lines print on the cover. Leave any blank to skip it. Advisor details are shared across your firm.</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            {FIELDS.map(([k, label, ph]) => (
              <div key={k} className="space-y-1.5">
                <Label htmlFor={`fb-${k}`}>{label}</Label>
                <Input id={`fb-${k}`} value={v[k] || ""} maxLength={k === "contact" ? 160 : 120} placeholder={ph}
                  onChange={(e) => setV({ ...v, [k]: e.target.value })} className="rounded-sm" data-testid={`family-book-${k.replace("_", "-")}-input`} />
              </div>
            ))}
            {firm?.can_edit ? (
              <label className="flex items-center gap-2 text-sm text-slate-700">
                <Checkbox checked={saveFirm} onCheckedChange={(c) => setSaveFirm(!!c)} data-testid="family-book-save-firm-checkbox" />
                Save advisor details for everyone in the firm
              </label>
            ) : (
              <p className="text-xs text-slate-500" data-testid="family-book-firm-note">Advisor details come from your firm admin. Changes here apply to this download only.</p>
            )}
            {firm?.can_edit && <EmailToClient api={api} period={period} cover={v} />}
          </div>
          <DialogFooter>
            <Button variant="ghost" className="rounded-sm" onClick={() => setOpen(false)} data-testid="family-book-cancel">Cancel</Button>
            <Button className="rounded-sm" onClick={go} disabled={busy} data-testid="family-book-download">{busy ? "Preparing…" : "Download PDF"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};
